import { ChevronDown, GripHorizontal, X } from "lucide-react";
import { useRef, useState, type ReactNode } from "react";

import { cn } from "@/lib/utils";

/** A floating, draggable, collapsible card that hovers OVER the document — so a
 * tablet user can scan the page, ask the AI, then move or collapse the answer
 * out of the way while annotating. Dragged by its header; constrained to its
 * positioned parent. */
export function DraggablePanel({
  title,
  label,
  onClose,
  children,
  initial = { x: 16, y: 16 },
  width = 360,
}: {
  title: ReactNode;
  /** Accessible name for the panel and its close button. Needed when `title` is
   * markup rather than a plain string — without it every panel announced itself
   * as "Assistant", which was fine while there was only one. */
  label?: string;
  onClose: () => void;
  children: ReactNode;
  initial?: { x: number; y: number };
  width?: number;
}) {
  const name = label ?? (typeof title === "string" ? title : "Assistant");
  const [pos, setPos] = useState(initial);
  const [collapsed, setCollapsed] = useState(false);
  const dragRef = useRef<{ dx: number; dy: number; w: number; h: number } | null>(null);
  const elRef = useRef<HTMLDivElement | null>(null);

  const onPointerDown = (e: React.PointerEvent) => {
    const el = elRef.current;
    const parent = el?.parentElement;
    if (!el || !parent) return;
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    const p = parent.getBoundingClientRect();
    dragRef.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y, w: p.width, h: p.height };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    const el = elRef.current;
    if (!d || !el) return;
    const maxX = Math.max(0, d.w - el.offsetWidth);
    const maxY = Math.max(0, d.h - 44);
    setPos({
      x: Math.min(maxX, Math.max(0, e.clientX - d.dx)),
      y: Math.min(maxY, Math.max(0, e.clientY - d.dy)),
    });
  };
  const onPointerUp = () => {
    dragRef.current = null;
  };

  return (
    <div
      ref={elRef}
      role="dialog"
      aria-label={name}
      className="absolute z-30 flex max-h-[calc(100%-1rem)] flex-col overflow-hidden rounded-lg border border-primary/30 bg-card/95 shadow-deep backdrop-blur-md"
      style={{ left: pos.x, top: pos.y, width }}
    >
      <header
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        className="flex shrink-0 cursor-move touch-none select-none items-center gap-1.5 border-b border-border bg-muted/60 px-2.5 py-1.5"
      >
        <GripHorizontal className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="truncate font-mono text-2xs uppercase tracking-widest text-muted-foreground">
          {title}
        </span>
        <div className="ml-auto flex items-center gap-0.5">
          <button
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? "Expand" : "Collapse"}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", collapsed && "-rotate-90")} />
          </button>
          <button
            onClick={onClose}
            aria-label={`Close ${name}`}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </header>
      {!collapsed && <div className="flex min-h-0 flex-1 flex-col">{children}</div>}
    </div>
  );
}
